import * as React from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '../lib/utils';

const Tabs = TabsPrimitive.Root;

type TabsVariant = 'default' | 'underline';

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsVariant;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', ...props }, ref) => {
  const baseClasses =
    'vai-inline-flex vai-items-center vai-text-sm vai-font-medium vai-transition-all focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2 disabled:vai-pointer-events-none disabled:vai-opacity-50';

  const variantClasses = {
    default:
      'vai-h-10 vai-rounded-md vai-bg-muted vai-p-1 vai-text-muted-foreground vai-justify-center',
    underline:
      'vai-bg-transparent vai-p-0 vai-h-auto vai-border-b vai-border-gray-200 vai-w-full vai-justify-start',
  };

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(baseClasses, variantClasses[variant], className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: TabsVariant;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = 'default', ...props }, ref) => {
  const baseClasses =
    'vai-inline-flex vai-items-center vai-justify-center vai-whitespace-nowrap vai-text-sm vai-font-medium vai-ring-offset-background vai-transition-all focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2 disabled:vai-pointer-events-none disabled:vai-opacity-50';

  const variantClasses = {
    default:
      'vai-rounded-sm vai-px-3 vai-py-1.5 data-[state=active]:vai-bg-background data-[state=active]:vai-text-foreground data-[state=active]:vai-shadow-sm',
    underline:
      'vai-px-6 vai-py-2 vai-bg-transparent vai-border-b-2 vai-border-transparent vai-rounded-none data-[state=active]:vai-border-b-2 data-[state=active]:vai-border-blue-500 data-[state=active]:vai-text-foreground data-[state=inactive]:vai-text-gray-500 hover:vai-text-gray-700',
  };

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(baseClasses, variantClasses[variant], className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  variant?: TabsVariant;
}

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, variant = 'default', ...props }, ref) => {
  const baseClasses =
    'vai-ring-offset-background focus-visible:vai-outline-none focus-visible:vai-ring-2 focus-visible:vai-ring-ring focus-visible:vai-ring-offset-2';

  const variantClasses = {
    default: 'vai-mt-2',
    underline: 'vai-mt-6',
  };

  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(baseClasses, variantClasses[variant], className)}
      {...props}
    />
  );
});
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsVariant };
